"use client";

// THE SHARE STUDIO, made small. Its real words and its real controls: the
// page on the left exactly as whoever you hand the link to meets it, and on the
// right who it opens for, what it's called, the ground it opens in, and the
// link. Abstract where the real one is personal (no real token, no real page):
// the link reads innernet.live/share/… and copying it copies the community.

import { useState } from "react";
import BrandIcon from "../../BrandIcon";
import { nettiSrc } from "./nettiSrc";
import { LinkBar } from "../../skills/PasteUrl";

const GROUNDS = [
  { name: "cream", ground: "#fffaf1", ink: "hsl(217 40% 18%)", hue: 217 },
  { name: "sage", ground: "hsl(132 52% 86%)", ink: "hsl(191 46% 16%)", hue: 191 },
  { name: "sand", ground: "hsl(37 52% 86%)", ink: "hsl(96 42% 15%)", hue: 96 },
  { name: "sky", ground: "hsl(222 52% 86%)", ink: "hsl(281 34% 20%)", hue: 281 },
  { name: "midnight", ground: "hsl(232 32% 14%)", ink: "hsl(40 60% 92%)", hue: 250 },
];

export default function ShareEmbed() {
  const [open, setOpen] = useState<"link" | "private">("link");
  const [g, setG] = useState(1);
  const [title, setTitle] = useState("how we run a launch");
  const [live, setLive] = useState(true);
  const [copied, setCopied] = useState(false);
  const G = GROUNDS[g];
  const change = (fn: () => void) => { fn(); setLive(false); };

  return (
    <figure className="bx bx--wide" data-room="share">
      <div className="bx-stage bx-studio">
        {/* the page, as whoever opens the link meets it */}
        <div className="bx-studio__page" style={{ ["--pg" as string]: G.ground, ["--pi" as string]: G.ink, ["--ph" as string]: G.hue } as React.CSSProperties}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img className="bx-studio__netti" src={nettiSrc(G.hue, G.ground)} alt="" aria-hidden decoding="async" />
          <span className="bx-studio__by"><span className="bx-studio__cell" aria-hidden />a skill, shared by jonna</span>
          <span className="bx-studio__t">{title || "untitled"}</span>
          <span className="bx-studio__line" />
          <span className="bx-studio__line bx-studio__line--short" />
          <span className="bx-studio__paste">paste this link into your ai. it will know what to do.</span>
          <span className="bx-studio__line" />
          <span className="bx-studio__line bx-studio__line--mid" />
          <span className="bx-studio__save"><BrandIcon name="innernet" size={13} /> save it to your innernet</span>
        </div>

        {/* the controls, in the studio's own words */}
        <div className="bx-studio__side">
          <span className="bx-studio__h">share {title || "this page"}</span>
          <span className="bx-studio__sub">a read-only door into this memory. you decide what it looks like and who it opens for.</span>

          <label className="bx-studio__label" htmlFor="bx-title">what it&rsquo;s called</label>
          <input id="bx-title" className="input-soft bx-studio__input" value={title} maxLength={60} onChange={(e) => change(() => setTitle(e.target.value))} />

          <span className="bx-studio__label">who it opens for · takes effect at once</span>
          <div className="bx-studio__opts" role="radiogroup">
            {([["link", "anyone with the link", "people and AIs holding it can read this"], ["private", "only you", "the door stays closed to everyone else"]] as const).map(([k, a, b]) => (
              <button key={k} type="button" role="radio" aria-checked={open === k} className="bx-studio__opt" data-on={open === k || undefined} onClick={() => setOpen(k)}>
                <span className="bx-studio__opt-a">{a}</span>
                <span className="bx-studio__opt-b">{b}</span>
              </button>
            ))}
          </div>
          {open === "link" && <span className="bx-studio__note">this is a skill, and it&rsquo;s open, so it also appears on the community skills page, wearing your name. always the version you keep now, never a copy.</span>}

          <span className="bx-studio__label">the ground this link opens in</span>
          <div className="bx-studio__grounds" role="radiogroup" aria-label="the ground this link opens in">
            {GROUNDS.map((x, i) => (
              <button key={x.name} type="button" role="radio" aria-checked={g === i} aria-label={x.name} className="bx-studio__swatch" data-on={g === i || undefined} style={{ background: x.ground }} onClick={() => change(() => setG(i))} />
            ))}
          </div>

          <span className="bx-studio__label">{open === "link" ? "this link is live" : "this link opens for you only"}</span>
          <div className="bx-studio__link">
            <LinkBar
              url="https://innernet.live/community"
              shown={`/share/${open === "link" ? "k7…" : "••••"}`}
              size="compact"
              label="copy the link"
              done={copied}
              onCopy={async () => { try { await navigator.clipboard.writeText("https://innernet.live/community"); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch { /* the words are on screen */ } }}
            />
          </div>

          <div className="bx-studio__foot">
            <span className="bx-studio__status"><span className="ink-dot" aria-hidden />{live ? "live" : "not on the link yet"}</span>
            <button type="button" className="bx-studio__post" onClick={() => setLive(true)} disabled={live}>{live ? "update the link" : "post it live"}</button>
          </div>
        </div>
      </div>
      <figcaption className="bx-cap">the share studio, small. every word here is the studio&rsquo;s own; pick a ground and the page on the left opens in it. (a sketch: no page of yours is on this link.)</figcaption>
    </figure>
  );
}
